import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from 'api/api';
import TokenUtils from 'utils/TokenUtils';
import { addNotification } from './notificationsSlice';

export const getSubscriptions = createAsyncThunk(
  'subscriptions/getSubscriptions',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/subscriptions', { params });
      return { ...response.data, isLoadMore: params.page > 1 };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати пакети');
    }
  }
);

export const loadMoreSubscriptions = createAsyncThunk(
  'subscriptions/loadMoreSubscriptions',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/subscriptions', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати пакети');
    }
  }
);

export const getSubscriptionByClientID = createAsyncThunk(
  'subscriptions/getSubscriptionByClientID',
  async (clientID, { rejectWithValue }) => {
    try {
      const response = await api.get(`/subscriptions/${clientID}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати пакет клієнта');
    }
  }
);

export const getSubscriptionsByClientID = createAsyncThunk(
  'subscriptions/getSubscriptionsByClientID',
  async (clientID, { rejectWithValue }) => {
    try {
      const response = await api.get('/subscriptions', { params: { clientID } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати пакети клієнта');
    }
  }
);

export const createSubscription = createAsyncThunk(
  'subscriptions/createSubscription',
  async (subscriptionData, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.post('/subscriptions', subscriptionData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Підписку створено',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка створення пакету');
    }
  }
);

export const updateSubscription = createAsyncThunk(
  'subscriptions/updateSubscription',
  async ({ id, ...updateData }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.put(`/subscriptions/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Підписку оновлено',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка оновлення пакету');
    }
  }
);

export const deleteSubscription = createAsyncThunk(
  'subscriptions/deleteSubscription',
  async (subscriptionID, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      await api.delete(`/subscriptions/${subscriptionID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Підписку видалено',
          type: 'success',
        })
      );

      return subscriptionID;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка видалення пакету');
    }
  }
);

const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState: {
    subscriptions: [],
    currentSubscription: null,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    meta: {
      total: 0,
      page: 1,
      limit: 30,
      totalPages: 0,
      hasMore: false,
    },
    currentParams: null,
  },
  reducers: {
    clearCurrentSubscription: (state, action) => {
      state.currentSubscription = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptions.pending, (state, action) => {
        const isLoadMore = action.meta.arg.page > 1;
        if (isLoadMore) {
          state.isLoadingMore = true;
        } else {
          state.isLoading = true;
          state.subscriptions = [];
        }
        state.error = null;
      })
      .addCase(getSubscriptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        const { data, meta, isLoadMore } = action.payload;

        if (isLoadMore) {
          state.subscriptions = [...state.subscriptions, ...data];
        } else {
          state.subscriptions = data;
        }

        state.meta = meta;
        state.currentParams = action.meta.arg;
      })
      .addCase(getSubscriptions.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload;
      })

      .addCase(loadMoreSubscriptions.pending, (state) => {
        state.isLoadingMore = true;
        state.error = null;
      })
      .addCase(loadMoreSubscriptions.fulfilled, (state, action) => {
        state.isLoadingMore = false;
        const { data, meta } = action.payload;
        state.subscriptions = [...state.subscriptions, ...data];
        state.meta = meta;
      })
      .addCase(loadMoreSubscriptions.rejected, (state, action) => {
        state.isLoadingMore = false;
        state.error = action.payload;
      })

      .addCase(getSubscriptionByClientID.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSubscription = action.payload;
      })
      .addCase(getSubscriptionByClientID.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(getSubscriptionsByClientID.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSubscriptionsByClientID.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, meta } = action.payload;
        state.subscriptions = data;
        state.meta = meta;
      })
      .addCase(getSubscriptionsByClientID.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(createSubscription.fulfilled, (state, action) => {
        state.currentSubscription = action.payload;
        state.subscriptions.unshift(action.payload);
      })
      .addCase(createSubscription.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateSubscription.fulfilled, (state, action) => {
        const index = state.subscriptions.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.subscriptions[index] = action.payload;
        }
        if (state.currentSubscription?.id === action.payload.id) {
          state.currentSubscription = action.payload;
        }
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteSubscription.fulfilled, (state, action) => {
        if (state.currentSubscription?.id === action.payload) {
          state.currentSubscription = null;
        }
        state.subscriptions = state.subscriptions.filter((s) => s.id !== action.payload);
      })
      .addCase(deleteSubscription.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addMatcher(
        (action) =>
          (action.type.startsWith('subscriptions/createSubscription') ||
            action.type.startsWith('subscriptions/updateSubscription') ||
            action.type.startsWith('subscriptions/deleteSubscription')) &&
          action.type.endsWith('/pending'),
        (state) => {
          state.error = null;
        }
      );
  },
});

export const { clearCurrentSubscription } = subscriptionsSlice.actions;

export default subscriptionsSlice.reducer;
